import React, { useState, useCallback, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  CircularProgress,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client'
import {
  DELETE_DOCUMENT,
  GET_PERSONAL_DOCUMENTS,
  UPLOAD_PERSONAL_DOCUMENT,
} from 'src/services/profileServices'
import { COLORS } from 'src/utils/Themes/colors'
import ConfirmationModal from 'src/components/common/ConfirmationModal'
import { showNotificationMessage } from 'src/actions'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'

const FileUpload = ({
  label,
  multiple = false,
  onSave,
  documentType,
  uploadedDocuments,
  onUploadComplete,
  documentsLoading,
  userName,
  refetch,
}) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  const dispatch = useDispatch()
  const [files, setFiles] = useState([])
  const [editIndex, setEditIndex] = useState(null)
  const [editedName, setEditedName] = useState('')
  const [disableSave, setDisableSave] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [deletingFile, setDeletingFile] = useState({})

  const handleFileChange = useCallback(
    (event) => {
      setDisableSave(false)
      const selectedFiles = Array.from(event.target.files)
      setFiles((prevFiles) =>
        multiple ? [...prevFiles, ...selectedFiles] : selectedFiles
      )
    },
    [multiple]
  )

  const handleDelete = useCallback((index) => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== index)
      setDisableSave(updatedFiles.length === 0)
      return updatedFiles
    })
  }, [])

  const handleDownload = useCallback((file) => {
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  const handleEdit = useCallback((index, name) => {
    setEditIndex(index)
    setEditedName(name)
  }, [])

  const handleSaveEdit = useCallback(
    (index) => {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles]
        updatedFiles[index] = new File([updatedFiles[index]], editedName, {
          type: updatedFiles[index].type,
        })
        return updatedFiles
      })
      setEditIndex(null)
    },
    [editedName]
  )

  const [deleteDocument] = useMutation(DELETE_DOCUMENT, {
    onError: (data) => {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message: data?.deletePersonalDocument?.message,
        })
      )
    },
    onCompleted: (data) => {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.SUCCESS,
          message: data?.deletePersonalDocument?.message,
        })
      )
      setDeleteConfirmation(false), setDeletingFile({}), refetch()
    },
  })

  const handlePermenantDeletion = async (file) => {
    await deleteDocument({
      variables: {
        documentId: file?.data?.id,
        userName: userName,
        documentType: documentType,
      },
    })
  }

  const handleSave = async () => {
    setSaveLoading(true)
    try {
      await onSave(files, documentType)
      setFiles([])
      onUploadComplete?.()
      setDisableSave(true)
    } finally {
      setSaveLoading(false)
    }
  }

  return (
    <>
      {deleteConfirmation && (
        <ConfirmationModal
          id='confirmationModal'
          message='Are you sure to delete this document ?'
          open={deleteConfirmation}
          onClose={() => {
            setDeleteConfirmation(false)
          }}
          onSubmit={() => {
            handlePermenantDeletion(deletingFile)
          }}
        />
      )}
      <TableRow>
        <TableCell className={classes.labelCell}>{label}</TableCell>
        <TableCell align='center'>
          <input
            type='file'
            multiple={multiple}
            onChange={handleFileChange}
            className={classes.inputField}
            id={label}
            ref={fileInputRef}
          />
          <label htmlFor={label}>
            <CloudUploadIcon className={classes.uploadIcon} />
          </label>
        </TableCell>
        <TableCell>
          {files.length > 0
            ? files.map((file, index) => (
                <div key={index} className={classes.uploadingFilesContainer}>
                  {editIndex === index ? (
                    <TextField
                      size='small'
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={(event) =>
                        event.key === 'Enter' && handleSaveEdit(index)
                      }
                    />
                  ) : (
                    <span>{file.name}</span>
                  )}
                  <div>
                    <IconButton
                      color='primary'
                      onClick={() => handleDownload(file)}
                    >
                      <DownloadIcon />
                    </IconButton>
                    {editIndex === index ? (
                      <IconButton
                        color='success'
                        onClick={() => handleSaveEdit(index)}
                      >
                        <CheckIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        color='primary'
                        onClick={() => handleEdit(index, file.name)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    <IconButton
                      color='error'
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
              ))
            : '-'}
        </TableCell>
        <TableCell>
          {documentsLoading ? (
            <CircularProgress />
          ) : uploadedDocuments?.length ? (
            <div className={classes.savedFilesContainer}>
              {uploadedDocuments.map((doc, index) => (
                <div key={index} className={classes.savedFile}>
                  <span>{doc?.originalName}</span>
                  <div>
                    <IconButton
                      color='primary'
                      component='a'
                      href={doc?.url}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      color='error'
                      onClick={() => {
                        setDeletingFile(doc)
                        setDeleteConfirmation(true)
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          {saveLoading ? (
            <CircularProgress />
          ) : (
            <Button
              variant='contained'
              color='primary'
              disabled={disableSave || saveLoading}
              onClick={handleSave}
              className={classes.submitButton}
            >
              Save
            </Button>
          )}
        </TableCell>
      </TableRow>
    </>
  )
}

const DocumentsUpload = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const userInfo = useSelector((state) => state?.userInfo)
  const userName = userInfo?.userInfo?.userData?.userName

  const {
    data: documentsData,
    loading: documentsLoading,
    refetch,
  } = useQuery(GET_PERSONAL_DOCUMENTS(userName), {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  const [uploadDocument] = useMutation(UPLOAD_PERSONAL_DOCUMENT, {
    onError: (data) => {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message: data?.uploadPersonalDocument?.message,
        })
      )
    },
    onCompleted: (data) => {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.SUCCESS,
          message: data?.uploadPersonalDocument?.message,
        })
      )
      refetch()
    },
  })

  const handleSave = useCallback(
    async (files, documentType) => {
      if (!files.length) return alert('No files to save!')
      await uploadDocument({
        variables: { documents: files, documentType, userName },
      })
    },
    [uploadDocument, userName]
  )

  return (
    <>
      <p className={classes.sectionHeader}>Documents Section</p>
      <TableContainer className={classes.tableMainContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document Type</TableCell>
              <TableCell align='center'>Upload</TableCell>
              <TableCell>Uploading Files</TableCell>
              <TableCell>Saved Files</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <FileUpload
              label='Aadhaar'
              multiple={false}
              documentType='aadhar'
              onSave={handleSave}
              uploadedDocuments={
                documentsData?.fetchPersonalDocuments?.aadharDocument
                  ? [documentsData.fetchPersonalDocuments.aadharDocument]
                  : []
              }
              documentsLoading={documentsLoading}
              userName={userName}
              refetch={refetch}
            />
            <FileUpload
              label='PAN'
              multiple={false}
              documentType='pan'
              onSave={handleSave}
              uploadedDocuments={
                documentsData?.fetchPersonalDocuments?.panDocument
                  ? [documentsData.fetchPersonalDocuments.panDocument]
                  : []
              }
              documentsLoading={documentsLoading}
              userName={userName}
              refetch={refetch}
            />
            <FileUpload
              label='Other Documents'
              multiple={true}
              documentType='otherDocuments'
              onSave={handleSave}
              uploadedDocuments={
                documentsData?.fetchPersonalDocuments?.otherDocuments
              }
              documentsLoading={documentsLoading}
              userName={userName}
              refetch={refetch}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default DocumentsUpload

const useStyles = makeStyles({
  // FileUpload
  sectionHeader: {
    fontSize: '16px',
    fontWeight: 'bold',
  },

  submitButton: {
    textTransform: 'unset !important',
    background: `${COLORS.DARK_PINK} !important`,
    color: `${COLORS.WHITE} !important`,
    borderRadius: '7px !important',
    '&:hover': {
      backgroundColor: COLORS.DARK_PINK,
    },
    '&.Mui-disabled': {
      backgroundColor: `${COLORS.GREY} !important`,
      color: `${COLORS.LIGHT_GREY} !important`,
      opacity: 0.7,
    },
  },
  inputField: {
    display: 'none',
  },
  labelCell: {
    fontWeight: 'bold',
  },
  uploadIcon: {
    color: '#B92250',
    cursor: 'pointer',
  },
  uploadingFilesContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
  },
  savedFilesContainer: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
  },
  savedFile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    gap: '5px',
    border: '1px solid grey',
    padding: ' 0px 10px',
    borderRadius: '10px',
  },

  // DocUpload
  tableMainContainer: {
    border: '1px solid gray',
    borderRadius: '10px',
  },
})
